import { useState } from 'react';
import { Search } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { ResourceActivity, ResourceActivityUser } from '@/types/resourceActivity';

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatActor = (
  user: ResourceActivityUser | null | undefined
): string | null => {
  if (!user) return null;
  const name = [user.first_name, user.last_name]
    .map(p => p?.trim() ?? '')
    .filter(Boolean)
    .join(' ');
  return name || user.email_address?.trim() || null;
};

interface Props {
  activities: ResourceActivity[];
  isLoading: boolean;
}

const ResourceActivityList = ({ activities, isLoading }: Props) => {
  const [search, setSearch] = useState('');

  const filtered = activities.filter(a =>
    a.details.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-gray-900 text-xl">Recent Activity</h2>
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white text-sm"
          />
        </div>
      </div>
      <div className="max-h-[600px] overflow-y-auto pr-4">
        {isLoading ? (
          <div className="space-y-0">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="py-4 border-b border-gray-100">
                <Skeleton className="h-4 w-48 mb-2" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500">No activity found.</p>
        ) : (
          <div className="space-y-0">
            {filtered.map(activity => {
              const actor = formatActor(activity.user);
              return (
                <div
                  key={activity.id}
                  className="py-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 -mx-4 px-4 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {activity.title && (
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {activity.title}
                      </span>
                    )}
                    <span className="text-gray-900">{activity.details}</span>
                    {actor && (
                      <span className="text-gray-600 text-sm">by {actor}</span>
                    )}
                    <span className="text-gray-400 text-sm">
                      {formatDate(activity.created_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceActivityList;
